import type { JSX } from 'react';
import {
  Box, ButtonBase, IconButton, Tooltip, styled,
} from '@mui/material';
import { MinusIcon, PlusIcon } from 'lucide-react';
import CustomTypography from '@/components/Common/Typography';
import { corePalette } from '@/themes/colours';
import {
  DESIGN_BORDER,
  MONITORING_LABEL,
  MONITORING_TOOLTIP,
  TIMELINE_COLOURS,
} from '@/constants/PatientDashboard/patientTimeline';

const HeaderRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  width: '100%',
});

const TopRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
  minHeight: '34px',
});

const Controls = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
});

const ControlButton = styled(ButtonBase)({
  boxSizing: 'border-box',
  height: '32px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  padding: '12px 16px',
  borderRadius: '8px',
  background: corePalette.grey30,
  color: corePalette.offBlack100,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  letterSpacing: '0.25px',
  whiteSpace: 'nowrap',
  '&:hover': {
    background: corePalette.grey50,
  },
});

const ZoomGroup = styled(Box)({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  height: '34px',
  border: `1px solid ${DESIGN_BORDER}`,
  borderRadius: '9px',
  overflow: 'hidden',
});

const ZoomButton = styled(IconButton)({
  width: '44px',
  height: '32px',
  borderRadius: 0,
  color: corePalette.offBlack100,
  '&.Mui-disabled': {
    color: TIMELINE_COLOURS.iconMuted,
  },
  '&:not(:first-of-type)': {
    borderLeft: `1px solid ${DESIGN_BORDER}`,
  },
});

const PatientIdRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  minHeight: '20px',
});

interface IProps {
  patientId?: string;
  canZoomIn: boolean;
  canZoomOut: boolean;
  onFitAll: () => void;
  onMonitoring: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
}

export default function Header({
  patientId,
  canZoomIn,
  canZoomOut,
  onFitAll,
  onMonitoring,
  onZoomIn,
  onZoomOut,
}: IProps): JSX.Element {
  return (
    <HeaderRoot>
      <TopRow>
        <CustomTypography variant="h3" fontWeight="medium" sx={{ lineHeight: '140%' }}>
          Patient Timeline
        </CustomTypography>
        <Controls>
          <ControlButton disableRipple onClick={onFitAll}>
            Fit all
          </ControlButton>
          <Tooltip title={MONITORING_TOOLTIP} describeChild>
            <ControlButton disableRipple onClick={onMonitoring}>
              {MONITORING_LABEL}
            </ControlButton>
          </Tooltip>
          <ZoomGroup>
            <ZoomButton
              aria-label="Zoom out"
              disableRipple
              disabled={!canZoomOut}
              onClick={onZoomOut}
            >
              <MinusIcon size={15} />
            </ZoomButton>
            <ZoomButton
              aria-label="Zoom in"
              disableRipple
              disabled={!canZoomIn}
              onClick={onZoomIn}
            >
              <PlusIcon size={15} />
            </ZoomButton>
          </ZoomGroup>
        </Controls>
      </TopRow>
      <PatientIdRow>
        <CustomTypography variant="bodySmall" fontWeight="medium" sx={{ color: TIMELINE_COLOURS.idLabel }}>
          Patient ID(s):
        </CustomTypography>
        <CustomTypography variant="bodySmall" fontWeight="medium" sx={{ color: TIMELINE_COLOURS.idValue }}>
          {patientId}
        </CustomTypography>
      </PatientIdRow>
    </HeaderRoot>
  );
}
